// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.

/**
 * This class maps skeleton joint paths to skeleton indices. It is constructed with a skeleton and
 * returns the mapping from joint paths to skeleton indices via the `getJointIndex(jointPath)` function.
 *
 * It is used by AnimationClipInstance to map JointChannel joint paths to skeleton indices for a skeleton.
 */
class SkeletonJointIndexMap {
  /**
   * @param {Skeleton} skeleton reference skeleton.
   */
  constructor(skeleton) {
    if (!skeleton) {
      throw new Error('SkeletonJointIndexMap constructed without a skeleton');
    }

    // A dictionary mapping joint paths to indices for the given skeleton.
    this._jointIndices = {};

    // Cached array of _jointIndices keys, sorted by the joint indices.
    this._jointPaths = [];

    if (!skeleton._joints) {
      return;
    }

    // Create a dictionary mapping joint names to indices.
    for (const joint of skeleton._joints) {
      const jointPath = this._getJointPath(joint, skeleton);
      // Check for duplicate fullpaths and raise a warning if there are duplicate fullpaths.
      if (this._jointPaths.find(elem => elem === jointPath) !== undefined) {
        console.log(`[Sumerian] warning: duplicate joint path ${jointPath} found in skeleton - applied animations might not be bound correctly`)
      } else {
        this._jointPaths.push(jointPath);
        this._jointIndices[jointPath] = joint._index;
      }
    }

    // Sort _jointPaths so that if a channel's joint path matches more than one skeleton joint path,
    // the one with the lowest joint index is always returned.
    this._jointPaths.sort((a, b) => this._jointIndices[a] - this._jointIndices[b]);
  }

  /**
   * Recursive function to find the joint index matched to a given joint path. This function matches the first skeleton path
   * (ordered by joint index) that ends with the given jointPath. If no such match is found, the search is continued by chopping
   * off one path part from the given joint path and repeating.
   *
   * @param {string} jointPath joint path.
   * @returns {?number} jointIndex if found or null.
   */
  getJointIndex(jointPath) {
    if (!jointPath) {
      return null;
    }

    const matchedPath = this._jointPaths.find(testPath => testPath.endsWith(jointPath));
    if (matchedPath !== undefined) {
      // skeleton path matched - return the index.
      return this._jointIndices[matchedPath];
    } else {
      const jointParts = jointPath.split('|');
      // if we're at the leaf and there was no match, return null to indicate no match. Otherwise
      // chop off a parent path part from the jointPath and recurse.
      return jointParts.length <= 1 ? null : this.getJointIndex(jointParts.slice(1).join('|'));
    }
  }

  /**
   * Returns the joint path of the given joint in the given skeleton.
   *
   * @param {Joint} joint joint for which to compute joint path.
   * @param {Skeleton} skeleton {@link Skeleton}
   */
  _getJointPath(joint, skeleton) {
    let jointPath = joint._name;
    let parent = skeleton._joints.find(elem => elem._index === joint._parentIndex);

    while (parent) {
      jointPath = `${parent._name}|${jointPath}`;
      parent = skeleton._joints.find(elem => elem._index === parent._parentIndex);
    }

    return jointPath;
  }
}

export default SkeletonJointIndexMap;